import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import recipesApi from '../api/RecipesApi';
import einkaufszettelApi from '../api/Einkaufszettel';
import { useShoppingListWebSocket } from '../hooks/useShoppingListWebSocket';
import { offlineDb, STORES } from '../services/offlineDatabase';
import { offlineQueueService, EntityType, OperationType } from '../services/offlineQueueService';
import { archiveItem, ENTITY_TYPES, ARCHIVE_REASONS } from '../utils/archiveManager';

const RecipeContext = createContext();

export const useRecipe = () => {
  const context = useContext(RecipeContext);
  if (!context) {
    throw new Error('useRecipe must be used within a RecipeProvider');
  }
  return context;
};

// Mock recipe data - "Goldene Hühnersuppe" as example
const MOCK_RECIPE_DATA = {
  'recipe-1': {
    id: 'recipe-1',
    bookmarkId: 'bookmark-recipe-1',
    title: 'Goldene Hühnersuppe',
    baseServings: 3,
    baseServingsRange: '2-3',
    ingredients: [
      { name: 'Hähnchenfilet', quantity: 300, unit: 'g', note: '', alternatives: [] },
      { name: 'Tomaten', quantity: 2, unit: 'Stück', note: '', alternatives: [] },
      { name: 'Wasser oder Hühnerbrühe', quantity: 500, unit: 'ml', note: '', alternatives: ['Gemüsebrühe'] },
      { name: 'Salz', quantity: 0.5, unit: 'TL', note: '', alternatives: [] },
      { name: 'Pfeffer', quantity: 0.25, unit: 'TL', note: '', alternatives: [] },
      { name: 'Zwiebel', quantity: 1, unit: 'Stück', note: 'mittelgroß', alternatives: [] },
      { name: 'Karotte', quantity: 1, unit: 'Stück', note: '', alternatives: [] },
      { name: 'Sellerie', quantity: 0.5, unit: 'Stange', note: '', alternatives: [] }
    ]
  }
};

export const RecipeProvider = ({ children }) => {
  const { user } = useAuth();

  // Shopping cart: array of { recipeId, targetServings }
  const [shoppingCart, setShoppingCart] = useState([]);

  // Shopping lists: array of { id, name, createdAt, items: [...], checkedItems: Set }
  const [shoppingLists, setShoppingLists] = useState([]);

  // Archive: array of { id, bookmarkId, title, archivedAt, snapshot }
  const [archivedRecipes, setArchivedRecipes] = useState([]);

  // Backend recipes: array of Recipe objects from API
  const [recipes, setRecipes] = useState([]);
  const [recipesLoading, setRecipesLoading] = useState(false);
  const [recipesError, setRecipesError] = useState(null);

  // Shopping lists loading state
  const [shoppingListsLoading, setShoppingListsLoading] = useState(false);
  const [shoppingListsError, setShoppingListsError] = useState(null);

  // Track if data has been loaded for current user (prevents overwriting)
  const isLoadedRef = useRef(false);
  const currentUserRef = useRef(null);

  // Track pending operations to ignore WebSocket events for own actions
  const pendingOperationsRef = useRef(new Set());

  // Subscribe to OfflineQueue events to replace temp IDs with real IDs after sync
  useEffect(() => {
    const unsubscribe = offlineQueueService.subscribe(async (event) => {
      if (event.type === 'ITEM_SYNCED' && event.item?.entityType === EntityType.RECIPE) {
        const { tempId } = event.item;
        const realRecipe = event.result;

        if (tempId && realRecipe?.id && event.item.operation === OperationType.CREATE) {
          console.log('[RecipeContext] Replacing temp recipe:', tempId, '->', realRecipe.id);

          // Replace temp recipe with real one in state
          setRecipes(prev => prev.map(r =>
            r.id === tempId ? { ...realRecipe, _isTemp: false, _pendingSync: false } : r
          ));

          // Update IndexedDB: remove temp, add real
          try {
            await offlineDb.delete(STORES.RECIPES, tempId);
            await offlineDb.put(STORES.RECIPES, realRecipe);
          } catch (cacheErr) {
            console.warn('[RecipeContext] IndexedDB update failed:', cacheErr);
          }
        }
      }

      // Handle shopping list sync
      if (event.type === 'ITEM_SYNCED' && event.item?.entityType === EntityType.SHOPPING_LIST) {
        const { tempId } = event.item;
        const realList = event.result;

        if (tempId && realList?.id && event.item.operation === OperationType.CREATE) {
          console.log('[RecipeContext] Replacing temp shopping list:', tempId, '->', realList.id);

          setShoppingLists(prev => prev.map(l =>
            l.id === tempId ? { ...realList, checkedItems: new Set(realList.checkedItems || []), _isTemp: false } : l
          ));

          try {
            await offlineDb.delete(STORES.SHOPPING_LISTS, tempId);
            await offlineDb.put(STORES.SHOPPING_LISTS, realList);
          } catch (cacheErr) {
            console.warn('[RecipeContext] Shopping list IndexedDB update failed:', cacheErr);
          }

          // Update localStorage if this was the QuickShoppingList/QuickTodoList
          // default oder secondary Listenzeiger (beide Komponenten merken sich
          // "ihre" aktuelle Liste per ID in localStorage - siehe QuickShoppingList.js
          // bzw. QuickTodoList.js - und müssen nach dem Sync von temp- auf die
          // echte Backend-ID aktualisiert werden, sonst verliert die Ansicht
          // ihre gerade erst angelegte Liste wieder aus den Augen).
          ['quickShoppingListDefaultId', 'quickShoppingListSecondaryId',
           'quickTodoListDefaultId', 'quickTodoListSecondaryId'].forEach(key => {
            if (localStorage.getItem(key) === tempId) {
              localStorage.setItem(key, String(realList.id));
              console.log(`[RecipeContext] Updated ${key}:`, tempId, '->', realList.id);
            }
          });
        }
      }
    });

    return () => unsubscribe();
  }, []);

  // Fetch all recipes from backend with IndexedDB cache fallback
  const fetchRecipes = useCallback(async () => {
    setRecipesLoading(true);
    setRecipesError(null);
    try {
      const data = await recipesApi.list();

      // Merge with any temp recipes that haven't synced yet
      setRecipes(prev => {
        const tempRecipes = prev.filter(r => r._isTemp);
        const backendIds = new Set((data || []).map(r => String(r.id)));
        // Keep temp recipes that don't have a matching backend ID yet
        const unSyncedTempRecipes = tempRecipes.filter(r => !backendIds.has(String(r.id)));
        return [...unSyncedTempRecipes, ...(data || [])];
      });

      // Cache in IndexedDB for offline access
      try {
        // Clear old cache and save new data
        await offlineDb.clear(STORES.RECIPES);
        for (const recipe of (data || [])) {
          await offlineDb.put(STORES.RECIPES, recipe);
        }
        // Also save temp recipes back to cache
        const currentRecipes = await offlineDb.getAll(STORES.RECIPES);
        const tempRecipeIds = new Set(currentRecipes.filter(r => r._isTemp).map(r => r.id));
        // Re-save temp recipes that were cleared
        setRecipes(prev => {
          prev.filter(r => r._isTemp).forEach(async (tempRecipe) => {
            try {
              await offlineDb.put(STORES.RECIPES, tempRecipe);
            } catch (e) { /* ignore */ }
          });
          return prev;
        });
      } catch (cacheErr) {
        console.warn('[Offline] Recipe cache update failed:', cacheErr);
      }
    } catch (err) {
      console.error('Error fetching recipes:', err);

      // Offline fallback: Load from IndexedDB cache
      try {
        const cachedRecipes = await offlineDb.getAll(STORES.RECIPES);
        if (cachedRecipes && cachedRecipes.length > 0) {
          console.log('[Offline] Loaded', cachedRecipes.length, 'recipes from cache');
          // Merge with existing temp recipes in state
          setRecipes(prev => {
            const existingTempRecipes = prev.filter(r => r._isTemp);
            const cachedIds = new Set(cachedRecipes.map(r => String(r.id)));
            // Keep temp recipes that aren't in cache (just created)
            const newTempRecipes = existingTempRecipes.filter(r => !cachedIds.has(String(r.id)));
            return [...newTempRecipes, ...cachedRecipes];
          });
          setRecipesError(null); // Clear error since we have cached data
        } else {
          // Keep any existing temp recipes
          setRecipes(prev => {
            const tempRecipes = prev.filter(r => r._isTemp);
            if (tempRecipes.length === 0) {
              setRecipesError(err.message);
            }
            return tempRecipes;
          });
        }
      } catch (cacheErr) {
        console.warn('[Offline] Cache read failed:', cacheErr);
        // Keep any existing temp recipes
        setRecipes(prev => {
          const tempRecipes = prev.filter(r => r._isTemp);
          if (tempRecipes.length === 0) {
            setRecipesError(err.message);
          }
          return tempRecipes;
        });
      }
    } finally {
      setRecipesLoading(false);
    }
  }, []);

  // Fetch all shopping lists from backend with IndexedDB cache fallback
  const fetchShoppingLists = useCallback(async () => {
    setShoppingListsLoading(true);
    setShoppingListsError(null);
    try {
      const data = await einkaufszettelApi.list();
      // Convert checkedItems array to Set for internal use
      const listsWithSets = (data || []).map(list => ({
        ...list,
        checkedItems: new Set(list.checkedItems || []),
        // Map 'owner' from backend to 'isOwner' for frontend consistency
        isOwner: list.owner
      }));

      // Preserve temp shopping lists that haven't synced yet
      let existingTempLists = [];
      try {
        const cachedLists = await offlineDb.getAll(STORES.SHOPPING_LISTS);
        existingTempLists = cachedLists.filter(l => l._isTemp);
        console.log('[ShoppingLists] Found', existingTempLists.length, 'temp lists to preserve');
      } catch (cacheErr) {
        console.warn('[Offline] Shopping lists cache read failed:', cacheErr);
      }

      // Merge: backend data + temp lists
      const tempListsWithSets = existingTempLists.map(list => ({
        ...list,
        checkedItems: new Set(list.checkedItems || [])
      }));
      const finalLists = [...listsWithSets, ...tempListsWithSets];
      setShoppingLists(finalLists);

      // Cache in IndexedDB (but keep temp lists)
      try {
        const cachedLists = await offlineDb.getAll(STORES.SHOPPING_LISTS);
        // Only delete non-temp lists
        for (const cached of cachedLists) {
          if (!cached._isTemp) {
            await offlineDb.delete(STORES.SHOPPING_LISTS, cached.id);
          }
        }
        // Add fresh backend data
        for (const list of (data || [])) {
          await offlineDb.put(STORES.SHOPPING_LISTS, list);
        }
      } catch (cacheErr) {
        console.warn('[Offline] Shopping lists cache update failed:', cacheErr);
      }

      return finalLists;
    } catch (err) {
      console.error('Error fetching shopping lists:', err);

      // Offline fallback: Load from IndexedDB cache
      try {
        const cachedLists = await offlineDb.getAll(STORES.SHOPPING_LISTS);
        if (cachedLists && cachedLists.length > 0) {
          console.log('[Offline] Loaded', cachedLists.length, 'shopping lists from cache');
          const listsWithSets = cachedLists.map(list => ({
            ...list,
            checkedItems: new Set(list.checkedItems || []),
            // Map 'owner' from backend to 'isOwner' for frontend consistency
            isOwner: list.owner ?? list.isOwner
          }));
          setShoppingLists(listsWithSets);
          setShoppingListsError(null); // Clear error since we have cached data
          return listsWithSets;
        } else {
          setShoppingListsError(err.message);
          setShoppingLists([]);
          return [];
        }
      } catch (cacheErr) {
        console.warn('[Offline] Cache read failed:', cacheErr);
        setShoppingListsError(err.message);
        setShoppingLists([]);
        return [];
      }
    } finally {
      setShoppingListsLoading(false);
    }
  }, []);

  // Migrate localStorage shopping lists to backend (one-time migration)
  const migrateLocalStorageLists = useCallback(async (userEmail) => {
    const migrationKey = `shoppingListsMigrated_${userEmail}`;
    const localStorageKey = `recipeShoppingLists_${userEmail}`;

    // Check if already migrated
    if (localStorage.getItem(migrationKey)) {
      return;
    }

    // Check for old localStorage data
    const savedLists = localStorage.getItem(localStorageKey);
    if (!savedLists) {
      // No old data to migrate, mark as done
      localStorage.setItem(migrationKey, 'true');
      return;
    }

    try {
      const localLists = JSON.parse(savedLists);
      if (!Array.isArray(localLists) || localLists.length === 0) {
        localStorage.setItem(migrationKey, 'true');
        return;
      }

      console.log(`Migrating ${localLists.length} shopping lists to backend...`);

      // Migrate each list to backend
      for (const list of localLists) {
        try {
          const payload = {
            name: list.name || 'Migrierte Liste',
            description: list.description || '',
            isManual: list.isManual !== false,
            recipeId: list.recipeId || null,
            recipeTitle: list.recipeTitle || null,
            targetServings: list.targetServings || null,
            items: (list.items || []).map(item => ({
              name: item.name || '',
              quantity: item.quantity || 0,
              unit: item.unit || '',
              note: item.note || '',
              alternatives: item.alternatives || [],
              originalQuantity: item.originalQuantity
            })),
            checkedItems: list.checkedItems
              ? (list.checkedItems instanceof Set
                  ? Array.from(list.checkedItems)
                  : Array.isArray(list.checkedItems)
                    ? list.checkedItems
                    : [])
              : []
          };

          await einkaufszettelApi.create(payload);
        } catch (err) {
          console.error('Error migrating list:', list.name, err);
        }
      }

      // Mark migration as complete
      localStorage.setItem(migrationKey, 'true');
      // Optionally remove old data
      localStorage.removeItem(localStorageKey);

      console.log('Shopping lists migration complete!');
    } catch (err) {
      console.error('Error during shopping lists migration:', err);
    }
  }, []);

  // Load data when user changes
  useEffect(() => {
    // Reset loaded flag when user changes
    if (user?.email !== currentUserRef.current) {
      isLoadedRef.current = false;
      currentUserRef.current = user?.email || null;
    }

    if (user?.email && !isLoadedRef.current) {
      // Load cart and archive from localStorage (local-only data)
      const cartKey = `recipeShoppingCart_${user.email}`;
      const archiveKey = `recipeArchive_${user.email}`;

      const savedCart = localStorage.getItem(cartKey);
      const savedArchive = localStorage.getItem(archiveKey);

      if (savedCart) {
        try {
          setShoppingCart(JSON.parse(savedCart));
        } catch (error) {
          console.error('Error loading shopping cart:', error);
          setShoppingCart([]);
        }
      } else {
        setShoppingCart([]);
      }

      if (savedArchive) {
        try {
          setArchivedRecipes(JSON.parse(savedArchive));
        } catch (error) {
          console.error('Error loading recipe archive:', error);
          setArchivedRecipes([]);
        }
      } else {
        setArchivedRecipes([]);
      }

      // Mark as loaded AFTER setting local state
      isLoadedRef.current = true;

      // Fetch recipes and shopping lists from backend
      fetchRecipes();

      // First migrate any old localStorage lists, then fetch from backend
      const initShoppingLists = async () => {
        await migrateLocalStorageLists(user.email);
        await fetchShoppingLists();
      };
      initShoppingLists();
    } else if (!user) {
      // No user logged in, reset to empty state
      setShoppingCart([]);
      setShoppingLists([]);
      setArchivedRecipes([]);
      setRecipes([]);
      isLoadedRef.current = false;
      currentUserRef.current = null;
    }
  }, [user, fetchRecipes, fetchShoppingLists, migrateLocalStorageLists]);

  // Save to localStorage whenever state changes - only after initial load
  useEffect(() => {
    if (user?.email && isLoadedRef.current) {
      const cartKey = `recipeShoppingCart_${user.email}`;
      localStorage.setItem(cartKey, JSON.stringify(shoppingCart));
    }
  }, [shoppingCart, user]);

  // Note: shoppingLists are now saved to backend, not localStorage

  useEffect(() => {
    if (user?.email && isLoadedRef.current) {
      const archiveKey = `recipeArchive_${user.email}`;
      localStorage.setItem(archiveKey, JSON.stringify(archivedRecipes));
    }
  }, [archivedRecipes, user]);

  // WebSocket callbacks for real-time updates
  const handleWebSocketListUpdate = useCallback((data) => {
    console.log('WebSocket: List update received', data);
    // Refresh lists to get the latest data
    fetchShoppingLists();
  }, [fetchShoppingLists]);

  const handleWebSocketItemToggle = useCallback((data) => {
    console.log('WebSocket: Item toggle received', data);
    const { listId, itemIndex, checked } = data;
    setShoppingLists(prevLists => prevLists.map(list => {
      if (String(list.id) === String(listId)) {
        const newCheckedItems = new Set(list.checkedItems);
        if (checked) {
          newCheckedItems.add(itemIndex);
        } else {
          newCheckedItems.delete(itemIndex);
        }
        return { ...list, checkedItems: newCheckedItems };
      }
      return list;
    }));
  }, []);

  const handleWebSocketItemAdded = useCallback((data) => {
    console.log('WebSocket: Item added received', data);
    const { listId, itemIndex } = data;
    const operationKey = `add-${listId}-${data.item?.name || itemIndex}`;

    // Ignore if this is our own operation (already handled optimistically)
    if (pendingOperationsRef.current.has(operationKey)) {
      console.log('WebSocket: Ignoring own add operation', operationKey);
      pendingOperationsRef.current.delete(operationKey);
      return;
    }

    // For remote changes, update state directly instead of full refresh
    if (data.item && listId) {
      setShoppingLists(prevLists => prevLists.map(list => {
        if (String(list.id) === String(listId)) {
          // Check if item already exists to avoid duplicates
          const exists = list.items?.some(i => i.name === data.item.name);
          if (exists) return list;
          return {
            ...list,
            items: [...(list.items || []), data.item]
          };
        }
        return list;
      }));
    } else {
      // Fallback: delayed refresh for incomplete data
      setTimeout(() => fetchShoppingLists(), 500);
    }
  }, [fetchShoppingLists]);

  const handleWebSocketItemRemoved = useCallback((data) => {
    console.log('WebSocket: Item removed received', data);
    const { listId, itemIndex } = data;
    const operationKey = `remove-${listId}-${itemIndex}`;

    // Ignore if this is our own operation (already handled optimistically)
    if (pendingOperationsRef.current.has(operationKey)) {
      console.log('WebSocket: Ignoring own remove operation', operationKey);
      pendingOperationsRef.current.delete(operationKey);
      return;
    }

    // For remote changes, update state directly
    if (listId !== undefined && itemIndex !== undefined) {
      setShoppingLists(prevLists => prevLists.map(list => {
        if (String(list.id) === String(listId)) {
          const newItems = [...(list.items || [])];
          newItems.splice(itemIndex, 1);
          // Update checked items indices
          const newChecked = new Set();
          list.checkedItems?.forEach(idx => {
            if (idx < itemIndex) newChecked.add(idx);
            else if (idx > itemIndex) newChecked.add(idx - 1);
          });
          return { ...list, items: newItems, checkedItems: newChecked };
        }
        return list;
      }));
    } else {
      // Fallback: delayed refresh
      setTimeout(() => fetchShoppingLists(), 500);
    }
  }, [fetchShoppingLists]);

  // Initialize WebSocket connection
  const { subscribe: wsSubscribe, unsubscribe: wsUnsubscribe, isConnected: wsConnected } = useShoppingListWebSocket({
    onListUpdate: handleWebSocketListUpdate,
    onItemToggle: handleWebSocketItemToggle,
    onItemAdded: handleWebSocketItemAdded,
    onItemRemoved: handleWebSocketItemRemoved,
    onCollaboratorJoined: (data) => {
      console.log('WebSocket: Collaborator joined', data);
      fetchShoppingLists();
    },
    onCollaboratorLeft: (data) => {
      console.log('WebSocket: Collaborator left', data);
      fetchShoppingLists();
    }
  });

  // Subscribe to all shopping lists when they change
  useEffect(() => {
    if (wsConnected && shoppingLists.length > 0) {
      shoppingLists.forEach(list => {
        wsSubscribe(list.id);
      });
    }
  }, [wsConnected, shoppingLists, wsSubscribe]);

  // Initialize Offline-Queue API Handlers
  useEffect(() => {
    // Initialize IndexedDB
    offlineDb.init().catch(err => {
      console.warn('[RecipeContext] IndexedDB init failed:', err);
    });

    // Register Shopping List API Handler
    offlineQueueService.registerApiHandler(EntityType.SHOPPING_LIST, {
      create: async (payload) => {
        const result = await einkaufszettelApi.create(payload);
        return result;
      },
      update: async (id, payload) => {
        const result = await einkaufszettelApi.update(id, payload);
        return result;
      },
      delete: async (id) => {
        await einkaufszettelApi.remove(id);
        return { success: true };
      },
      getById: async (id) => {
        return await einkaufszettelApi.getById(id);
      }
    });

    // Register Shopping List Item API Handler
    offlineQueueService.registerApiHandler(EntityType.SHOPPING_LIST_ITEM, {
      create: async (payload) => {
        const result = await einkaufszettelApi.addItem(payload.listId, payload.item);
        return result;
      },
      update: async (id, payload) => {
        // For toggle operations
        if (payload.action === 'TOGGLE_CHECKED') {
          await einkaufszettelApi.toggleItemChecked(payload.listId, payload.itemIndex);
        }
        return { success: true };
      },
      delete: async (id, payload) => {
        await einkaufszettelApi.removeItem(payload?.listId, payload?.itemIndex);
        return { success: true };
      }
    });

    // Register Recipe API Handler
    offlineQueueService.registerApiHandler(EntityType.RECIPE, {
      create: async (payload) => {
        const result = await recipesApi.create(payload);
        return result;
      },
      update: async (id, payload) => {
        const result = await recipesApi.update(id, payload);
        return result;
      },
      delete: async (id) => {
        await recipesApi.remove(id);
        return { success: true };
      },
      getById: async (id) => {
        return await recipesApi.getById(id);
      }
    });
  }, []);

  // Add recipe to shopping cart
  const addToCart = (recipeId, targetServings) => {
    setShoppingCart(prev => {
      const existing = prev.find(item => item.recipeId === recipeId);
      if (existing) {
        return prev.map(item =>
          item.recipeId === recipeId
            ? { ...item, targetServings }
            : item
        );
      }
      return [...prev, { recipeId, targetServings }];
    });
  };

  // Remove from cart
  const removeFromCart = (recipeId) => {
    setShoppingCart(prev => prev.filter(item => item.recipeId !== recipeId));
  };

  // Clear cart
  const clearCart = () => {
    setShoppingCart([]);
  };

  // Get today's date formatted as "DD.MM.YYYY"
  const getTodayDateString = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    return `${day}.${month}.${year}`;
  };

  // Find or create today's shopping list and add recipe ingredients
  const addRecipeToTodaysList = async (recipe, targetServings) => {
    const todayDate = getTodayDateString();
    const todayListName = `${todayDate}_Einkaufen`;

    // Scale ingredients
    const scaledIngredients = scaleIngredients(
      recipe.ingredients || [],
      recipe.baseServings || 4,
      targetServings
    );

    // Convert to backend format
    const newItems = scaledIngredients.map(ing => ({
      name: ing.name,
      quantity: ing.quantity,
      unit: ing.unit,
      note: ing.note ? `${ing.note} (${recipe.title || recipe.name})` : `(${recipe.title || recipe.name})`,
      alternatives: ing.alternatives || [],
      originalQuantity: ing.originalQuantity
    }));

    // Check if today's list already exists
    const existingList = shoppingLists.find(list =>
      list.name === todayListName ||
      list.name.startsWith(`${todayDate}_`)
    );

    if (existingList) {
      // Add items to existing list
      try {
        // Merge items - combine quantities for same items
        const mergedItems = [...(existingList.items || [])];

        for (const newItem of newItems) {
          const existingItemIndex = mergedItems.findIndex(item =>
            item.name.toLowerCase() === newItem.name.toLowerCase() &&
            item.unit === newItem.unit
          );

          if (existingItemIndex >= 0) {
            // Add quantities together
            mergedItems[existingItemIndex] = {
              ...mergedItems[existingItemIndex],
              quantity: mergedItems[existingItemIndex].quantity + newItem.quantity,
              note: mergedItems[existingItemIndex].note
                ? `${mergedItems[existingItemIndex].note}, ${newItem.note}`
                : newItem.note
            };
          } else {
            // Add new item
            mergedItems.push(newItem);
          }
        }

        await updateShoppingList(existingList.id, { items: mergedItems });
        return existingList.name;
      } catch (err) {
        console.error('Error adding to existing list:', err);
        throw err;
      }
    } else {
      // Create new list for today
      const payload = {
        name: todayListName,
        description: `Einkaufszettel vom ${todayDate}`,
        isManual: false,
        listType: 'SHOPPING',
        items: newItems
      };

      try {
        const created = await einkaufszettelApi.create(payload);
        const listWithSet = {
          ...created,
          checkedItems: new Set(created.checkedItems || [])
        };
        setShoppingLists(prev => [listWithSet, ...prev]);
        return created.name;
      } catch (err) {
        console.error('Error creating today\'s list:', err);
        throw err;
      }
    }
  };

  // Rezept-Zutaten zu einer vom Nutzer gewählten Einkaufsliste hinzufügen -
  // entweder zu einer bestehenden Liste (listId gesetzt) oder als neue Liste
  // (listId leer, newListName optional - sonst Standardname mit heutigem Datum).
  const addRecipeIngredientsToList = async (recipe, targetServings, listId, newListName) => {
    const scaledIngredients = scaleIngredients(
      recipe.ingredients || [],
      recipe.baseServings || 4,
      targetServings
    );

    const newItems = scaledIngredients.map(ing => ({
      name: ing.name,
      quantity: ing.quantity,
      unit: ing.unit,
      note: ing.note ? `${ing.note} (${recipe.title || recipe.name})` : `(${recipe.title || recipe.name})`,
      alternatives: ing.alternatives || [],
      originalQuantity: ing.originalQuantity
    }));

    if (listId) {
      const existingList = shoppingLists.find(list => String(list.id) === String(listId));
      if (!existingList) {
        throw new Error('Einkaufsliste nicht gefunden');
      }

      // Mengen fuer gleiche Zutaten zusammenfassen
      const mergedItems = [...(existingList.items || [])];
      for (const newItem of newItems) {
        const existingItemIndex = mergedItems.findIndex(item =>
          item.name.toLowerCase() === newItem.name.toLowerCase() &&
          item.unit === newItem.unit
        );

        if (existingItemIndex >= 0) {
          mergedItems[existingItemIndex] = {
            ...mergedItems[existingItemIndex],
            quantity: mergedItems[existingItemIndex].quantity + newItem.quantity,
            note: mergedItems[existingItemIndex].note
              ? `${mergedItems[existingItemIndex].note}, ${newItem.note}`
              : newItem.note
          };
        } else {
          mergedItems.push(newItem);
        }
      }

      await updateShoppingList(existingList.id, { items: mergedItems });
      return existingList.name;
    }

    // Neue Liste erstellen
    const todayDate = getTodayDateString();
    const name = newListName?.trim() || `${todayDate}_Einkaufen`;
    const payload = {
      name,
      description: `Einkaufszettel vom ${todayDate}`,
      isManual: false,
      listType: 'SHOPPING',
      items: newItems
    };

    const created = await einkaufszettelApi.create(payload);
    const listWithSet = {
      ...created,
      checkedItems: new Set(created.checkedItems || [])
    };
    setShoppingLists(prev => [listWithSet, ...prev]);
    return created.name;
  };

  // Create shopping list from recipe (Backend)
  const createShoppingList = async (recipe, targetServings) => {
    const scaledIngredients = scaleIngredients(recipe.ingredients || [], recipe.baseServings || 4, targetServings);

    // Convert ingredients to backend format
    const backendItems = scaledIngredients.map(ing => ({
      name: ing.name,
      quantity: ing.quantity,
      unit: ing.unit,
      note: ing.note || '',
      alternatives: ing.alternatives || [],
      originalQuantity: ing.originalQuantity
    }));

    const payload = {
      name: `${recipe.title || recipe.name} (${targetServings} Portionen)`,
      isManual: false,
      listType: 'SHOPPING',
      recipeId: String(recipe.id),
      recipeTitle: recipe.title || recipe.name,
      targetServings,
      items: backendItems
    };

    try {
      const created = await einkaufszettelApi.create(payload);
      // Convert checkedItems to Set
      const listWithSet = {
        ...created,
        checkedItems: new Set(created.checkedItems || [])
      };
      setShoppingLists(prev => [listWithSet, ...prev]);

      // Sicherheits-Snapshot beim Anlegen (nur wenn "Automatische Datensicherung" aktiv ist).
      // Die Liste bleibt dabei unangetastet bestehen - der Snapshot ist ein zusätzliches
      // Backup, falls die Liste später mal nicht mehr vorhanden ist.
      archiveItem(
        ENTITY_TYPES.SHOPPING_LIST,
        ARCHIVE_REASONS.AUTO_SNAPSHOT,
        { ...listWithSet, checkedItems: Array.from(listWithSet.checkedItems || []) },
        { label: listWithSet.name }
      );

      return created.id;
    } catch (err) {
      console.error('Error creating shopping list:', err);
      throw err;
    }
  };

  // Toggle item checked (Backend-first when online, Queue when offline)
  const toggleItemChecked = async (listId, itemIndex) => {
    // Validate parameters
    if (!listId || listId === 'undefined' || itemIndex === undefined || itemIndex === null) {
      console.error('toggleItemChecked: Invalid parameters', { listId, itemIndex });
      return;
    }

    // ONLINE MODE: Direct API call, update with response
    if (navigator.onLine) {
      // Optimistic update for immediate feedback
      setShoppingLists(prev => prev.map(list => {
        if (String(list.id) === String(listId)) {
          const newChecked = new Set(list.checkedItems);
          if (newChecked.has(itemIndex)) {
            newChecked.delete(itemIndex);
          } else {
            newChecked.add(itemIndex);
          }
          return { ...list, checkedItems: newChecked };
        }
        return list;
      }));

      try {
        const updatedList = await einkaufszettelApi.toggleItemChecked(listId, itemIndex);
        // Update state with server response (source of truth)
        const listWithSet = {
          ...updatedList,
          checkedItems: new Set(updatedList.checkedItems || [])
        };
        setShoppingLists(prev => prev.map(list =>
          (String(list.id) === String(listId)) ? listWithSet : list
        ));
        // Update cache
        try {
          await offlineDb.put(STORES.SHOPPING_LISTS, updatedList);
        } catch (cacheErr) {
          console.warn('[Offline] Cache update failed:', cacheErr);
        }
      } catch (err) {
        console.error('Error toggling item:', err);
        // Revert optimistic update on error
        setShoppingLists(prev => prev.map(list => {
          if (String(list.id) === String(listId)) {
            const revertChecked = new Set(list.checkedItems);
            if (revertChecked.has(itemIndex)) {
              revertChecked.delete(itemIndex);
            } else {
              revertChecked.add(itemIndex);
            }
            return { ...list, checkedItems: revertChecked };
          }
          return list;
        }));
      }
      return;
    }

    // OFFLINE MODE: Optimistic update + Queue
    let previousList = null;
    setShoppingLists(prev => prev.map(list => {
      if (String(list.id) === String(listId)) {
        previousList = { ...list, checkedItems: new Set(list.checkedItems) };
        const newChecked = new Set(list.checkedItems);
        if (newChecked.has(itemIndex)) {
          newChecked.delete(itemIndex);
        } else {
          newChecked.add(itemIndex);
        }
        return { ...list, checkedItems: newChecked };
      }
      return list;
    }));

    try {
      const list = shoppingLists.find(l => String(l.id) === String(listId));
      if (list) {
        await offlineDb.put(STORES.SHOPPING_LISTS, {
          ...list,
          checkedItems: Array.from(list.checkedItems),
          _pendingSync: true
        });
      }
    } catch (cacheErr) {
      console.warn('[Offline] Cache update failed:', cacheErr);
    }

    await offlineQueueService.enqueue({
      entityType: EntityType.SHOPPING_LIST_ITEM,
      operation: OperationType.UPDATE,
      entityId: `${listId}-${itemIndex}`,
      payload: { listId, itemIndex, action: 'TOGGLE_CHECKED' }
    });
  };

  // Delete shopping list (Backend with Offline-Queue)
  const deleteShoppingList = async (listId) => {
    // Store previous state for rollback
    const previousList = shoppingLists.find(list => list.id === listId || String(list.id) === String(listId));

    // Papierkorb: gelöschte Einkaufsliste immer archivieren, damit sie wiederherstellbar
    // bleibt - awaited, damit das Archivieren VOR dem eigentlichen Löschen abgeschlossen ist
    if (previousList) {
      await archiveItem(
        ENTITY_TYPES.SHOPPING_LIST,
        ARCHIVE_REASONS.DELETED,
        { ...previousList, checkedItems: Array.from(previousList.checkedItems || []) },
        { label: previousList.name }
      );
    }

    // 1. Optimistic update in UI
    setShoppingLists(prev => prev.filter(list => list.id !== listId && String(list.id) !== String(listId)));

    // 2. Mark as deleted in IndexedDB cache
    try {
      if (previousList) {
        await offlineDb.put(STORES.SHOPPING_LISTS, {
          ...previousList,
          checkedItems: Array.from(previousList.checkedItems || []),
          _deleted: true,
          _pendingSync: true
        });
      }
    } catch (cacheErr) {
      console.warn('[Offline] Cache update failed:', cacheErr);
    }

    // 3. Queue for backend sync
    try {
      await offlineQueueService.enqueue({
        entityType: EntityType.SHOPPING_LIST,
        operation: OperationType.DELETE,
        entityId: listId
      });
    } catch (err) {
      console.error('Error deleting shopping list:', err);
      // Revert on error if online
      if (navigator.onLine && previousList) {
        setShoppingLists(prev => [previousList, ...prev]);
      }
    }
  };

  // Create manual shopping list (Backend with Offline-Queue)
  const createManualShoppingList = async (name, description = '', items = [], options = {}) => {
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    // listType: 'SHOPPING' oder 'TODO' - Aufrufer (QuickShoppingList.js bzw.
    // QuickTodoList.js) geben das immer explizit an. Fällt jemand diese
    // Angabe weg, ist 'TODO' der Default (siehe TODO_ShoppingListType_Backend.md).
    const listType = options.listType || 'TODO';

    const payload = {
      name,
      description,
      isManual: true,
      listType,
      items: items.map(item => ({
        name: item.name,
        quantity: item.quantity || 0,
        unit: item.unit || '',
        note: item.note || ''
      }))
    };

    // 1. Create temp list for optimistic UI
    const tempList = {
      id: tempId,
      ...payload,
      checkedItems: new Set(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      _isTemp: true,
      _pendingSync: true
    };

    // 2. Add to UI immediately
    setShoppingLists(prev => [tempList, ...prev]);

    // Sicherheits-Snapshot beim Anlegen (nur wenn "Automatische Datensicherung" aktiv ist).
    // Die Liste bleibt dabei unangetastet bestehen - der Snapshot ist ein zusätzliches
    // Backup, falls die Liste später mal nicht mehr vorhanden ist. Wird bei einer
    // Wiederherstellung aus dem Archiv übersprungen (skipArchiveSnapshot), damit das
    // Archiv nicht bei jedem Wiederherstellen einen neuen Eintrag anlegt.
    if (!options.skipArchiveSnapshot) {
      archiveItem(
        ENTITY_TYPES.SHOPPING_LIST,
        ARCHIVE_REASONS.AUTO_SNAPSHOT,
        { ...tempList, checkedItems: [] },
        { label: tempList.name }
      );
    }

    // 3. Save to IndexedDB cache
    try {
      await offlineDb.put(STORES.SHOPPING_LISTS, {
        ...tempList,
        checkedItems: []
      });
    } catch (cacheErr) {
      console.warn('[Offline] Cache update failed:', cacheErr);
    }

    // 4. Queue for backend sync
    try {
      await offlineQueueService.enqueue({
        entityType: EntityType.SHOPPING_LIST,
        operation: OperationType.CREATE,
        tempId,
        payload
      });

      return tempId;
    } catch (err) {
      console.error('Error creating manual shopping list:', err);
      // Remove temp list on immediate failure if online
      if (navigator.onLine) {
        setShoppingLists(prev => prev.filter(list => list.id !== tempId));
      }
      throw err;
    }
  };

  // Add item to shopping list (Backend-first when online, Queue when offline)
  const addItemToList = async (listId, item) => {
    // Validate listId to prevent undefined API calls
    if (!listId || listId === 'undefined') {
      console.error('addItemToList: Invalid listId', listId);
      throw new Error('Ungültige Listen-ID');
    }

    const newItem = {
      name: item.name,
      quantity: item.quantity || 0,
      unit: item.unit || '',
      note: item.note || ''
    };

    // Mark this operation as pending to ignore WebSocket echo
    const operationKey = `add-${listId}-${item.name}`;
    pendingOperationsRef.current.add(operationKey);
    setTimeout(() => pendingOperationsRef.current.delete(operationKey), 10000);

    // ONLINE MODE: Direct API call, update with response
    if (navigator.onLine) {
      try {
        const updatedList = await einkaufszettelApi.addItem(listId, newItem);
        // Update state with server response (source of truth)
        const listWithSet = {
          ...updatedList,
          checkedItems: new Set(updatedList.checkedItems || [])
        };
        setShoppingLists(prev => prev.map(list =>
          (String(list.id) === String(listId)) ? listWithSet : list
        ));
        // Update cache
        try {
          await offlineDb.put(STORES.SHOPPING_LISTS, updatedList);
        } catch (cacheErr) {
          console.warn('[Offline] Cache update failed:', cacheErr);
        }
        return;
      } catch (err) {
        console.error('Error adding item to list:', err);
        throw err;
      }
    }

    // OFFLINE MODE: Optimistic update + Queue (prepend item at beginning)
    setShoppingLists(prev => prev.map(list => {
      if (String(list.id) === String(listId)) {
        return {
          ...list,
          items: [newItem, ...(list.items || [])],
          _pendingSync: true
        };
      }
      return list;
    }));

    try {
      const list = shoppingLists.find(l => String(l.id) === String(listId));
      if (list) {
        await offlineDb.put(STORES.SHOPPING_LISTS, {
          ...list,
          items: [newItem, ...(list.items || [])],
          checkedItems: Array.from(list.checkedItems || []),
          _pendingSync: true
        });
      }
    } catch (cacheErr) {
      console.warn('[Offline] Cache update failed:', cacheErr);
    }

    await offlineQueueService.enqueue({
      entityType: EntityType.SHOPPING_LIST_ITEM,
      operation: OperationType.CREATE,
      parentId: listId,
      payload: { listId, item: newItem }
    });
  };

  // Remove item from shopping list (Backend-first when online, Queue when offline)
  const removeItemFromList = async (listId, itemIndex) => {
    // Validate parameters to prevent undefined API calls
    if (!listId || listId === 'undefined' || itemIndex === undefined || itemIndex === null) {
      console.error('removeItemFromList: Invalid parameters', { listId, itemIndex });
      throw new Error('Ungültige Parameter für Lösch-Operation');
    }

    // Get the item name before deletion for queue cleanup
    const list = shoppingLists.find(l => String(l.id) === String(listId));
    const itemToDelete = list?.items?.[itemIndex];
    const itemName = itemToDelete?.name;

    // Mark this operation as pending to ignore WebSocket echo
    const operationKey = `remove-${listId}-${itemIndex}`;
    pendingOperationsRef.current.add(operationKey);
    setTimeout(() => pendingOperationsRef.current.delete(operationKey), 10000);

    // Cancel any pending CREATE operations for this item in the queue
    // This prevents "item already exists" errors when old offline CREATEs are synced
    try {
      const cancelled = await offlineQueueService.cancelPendingCreatesForItem(
        EntityType.SHOPPING_LIST_ITEM,
        listId,
        itemName
      );
      if (cancelled > 0) {
        console.log(`[removeItemFromList] Cancelled ${cancelled} pending CREATE operation(s)`);
      }
    } catch (cleanupErr) {
      console.warn('[removeItemFromList] Queue cleanup warning:', cleanupErr);
    }

    // ONLINE MODE: Direct API call, update with response
    if (navigator.onLine) {
      try {
        const updatedList = await einkaufszettelApi.removeItem(listId, itemIndex);
        // Update state with server response (source of truth)
        const listWithSet = {
          ...updatedList,
          checkedItems: new Set(updatedList.checkedItems || [])
        };
        setShoppingLists(prev => prev.map(list =>
          (String(list.id) === String(listId)) ? listWithSet : list
        ));
        // Update cache
        try {
          await offlineDb.put(STORES.SHOPPING_LISTS, updatedList);
        } catch (cacheErr) {
          console.warn('[Offline] Cache update failed:', cacheErr);
        }
        return;
      } catch (err) {
        console.error('Error removing item from list:', err);
        throw err;
      }
    }

    // OFFLINE MODE: Optimistic update + Queue
    let previousList = null;
    setShoppingLists(prev => prev.map(list => {
      if (String(list.id) === String(listId)) {
        previousList = { ...list, checkedItems: new Set(list.checkedItems) };
        const newItems = [...(list.items || [])];
        newItems.splice(itemIndex, 1);
        const newChecked = new Set();
        (list.checkedItems || new Set()).forEach(idx => {
          if (idx < itemIndex) newChecked.add(idx);
          else if (idx > itemIndex) newChecked.add(idx - 1);
        });
        return { ...list, items: newItems, checkedItems: newChecked, _pendingSync: true };
      }
      return list;
    }));

    try {
      const list = shoppingLists.find(l => String(l.id) === String(listId));
      if (list) {
        const newItems = [...(list.items || [])];
        newItems.splice(itemIndex, 1);
        await offlineDb.put(STORES.SHOPPING_LISTS, {
          ...list,
          items: newItems,
          checkedItems: Array.from(list.checkedItems || []).filter(idx => idx !== itemIndex).map(idx => idx > itemIndex ? idx - 1 : idx),
          _pendingSync: true
        });
      }
    } catch (cacheErr) {
      console.warn('[Offline] Cache update failed:', cacheErr);
    }

    await offlineQueueService.enqueue({
      entityType: EntityType.SHOPPING_LIST_ITEM,
      operation: OperationType.DELETE,
      entityId: `${listId}-${itemIndex}`,
      payload: { listId, itemIndex, itemName }
    });
  };

  // Update shopping list (Backend)
  const updateShoppingList = async (listId, updates) => {
    try {
      // Convert checkedItems Set to Array if present
      const payload = { ...updates };
      if (updates.checkedItems instanceof Set) {
        payload.checkedItems = Array.from(updates.checkedItems);
      }

      const updated = await einkaufszettelApi.update(listId, payload);
      const listWithSet = {
        ...updated,
        checkedItems: new Set(updated.checkedItems || [])
      };
      setShoppingLists(prev => prev.map(list =>
        (list.id === listId || String(list.id) === String(listId)) ? listWithSet : list
      ));
    } catch (err) {
      console.error('Error updating shopping list:', err);
      throw err;
    }
  };

  // ===== Share Functions =====

  // Create a share link for a shopping list
  const shareShoppingList = async (listId, options = {}) => {
    try {
      const share = await einkaufszettelApi.createShare(listId, options);
      return share.shareUrl;
    } catch (err) {
      console.error('Error creating share:', err);
      throw err;
    }
  };

  // Join a shopping list via share token
  const joinSharedList = async (token) => {
    try {
      const response = await einkaufszettelApi.joinByToken(token);
      if (response.success) {
        // Refresh all lists from server to get complete data with items
        await fetchShoppingLists();
      }
      return response;
    } catch (err) {
      console.error('Error joining shared list:', err);
      throw err;
    }
  };

  // Revoke a share link
  const revokeShoppingListShare = async (token) => {
    try {
      await einkaufszettelApi.revokeShare(token);
    } catch (err) {
      console.error('Error revoking share:', err);
      throw err;
    }
  };

  // Get share info (public, no auth)
  const getShoppingListShareInfo = async (token) => {
    try {
      return await einkaufszettelApi.getShareInfo(token);
    } catch (err) {
      console.error('Error getting share info:', err);
      throw err;
    }
  };

  // Get active shares for a list
  const getActiveShares = async (listId) => {
    try {
      return await einkaufszettelApi.getActiveShares(listId);
    } catch (err) {
      console.error('Error getting active shares:', err);
      throw err;
    }
  };

  // Remove a collaborator from a list
  const removeCollaborator = async (listId, collaboratorId) => {
    try {
      await einkaufszettelApi.removeCollaborator(listId, collaboratorId);
      // Refresh list to get updated collaborators
      await fetchShoppingLists();
    } catch (err) {
      console.error('Error removing collaborator:', err);
      throw err;
    }
  };

  // Leave a shared list
  const leaveShoppingList = async (listId) => {
    try {
      await einkaufszettelApi.leaveList(listId);
      // Remove from local state
      setShoppingLists(prev => prev.filter(l => String(l.id) !== String(listId)));
    } catch (err) {
      console.error('Error leaving list:', err);
      throw err;
    }
  };

  // Archive recipe
  const archiveRecipe = (bookmark) => {
    const archived = {
      id: `archive-${Date.now()}`,
      bookmarkId: bookmark.id,
      title: bookmark.title,
      url: bookmark.url,
      description: bookmark.description,
      archivedAt: new Date().toISOString(),
      snapshot: {
        title: bookmark.title,
        url: bookmark.url,
        description: bookmark.description,
        category: bookmark.category
      }
    };

    setArchivedRecipes(prev => [archived, ...prev]);
    return archived.id;
  };

  // Get recipe data (from backend, mock, or could be from bookmark)
  const getRecipeData = (recipeId) => {
    // First check backend recipes
    const backendRecipe = recipes.find(r => r.id === recipeId);
    if (backendRecipe) return backendRecipe;
    // Fallback to mock data
    return MOCK_RECIPE_DATA[recipeId] || null;
  };

  // Create new recipe (with Offline-Queue support)
  const createRecipe = async (recipeData, options = {}) => {
    const tempId = `temp-recipe-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // 1. Create temp recipe for optimistic UI
    const tempRecipe = {
      id: tempId,
      ...recipeData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      _isTemp: true,
      _pendingSync: true
    };

    // 2. Add to UI immediately (optimistic update)
    setRecipes(prev => [tempRecipe, ...prev]);

    // Sicherheits-Snapshot beim Anlegen (nur wenn "Automatische Datensicherung" aktiv ist).
    // Wird bei einer Wiederherstellung aus dem Archiv übersprungen (skipArchiveSnapshot),
    // damit das Archiv nicht bei jedem Wiederherstellen einen neuen Eintrag anlegt.
    if (!options.skipArchiveSnapshot) {
      archiveItem(
        ENTITY_TYPES.RECIPE,
        ARCHIVE_REASONS.AUTO_SNAPSHOT,
        tempRecipe,
        { label: tempRecipe.title }
      );
    }

    // 3. Save to IndexedDB cache
    try {
      await offlineDb.put(STORES.RECIPES, tempRecipe);
    } catch (cacheErr) {
      console.warn('[Offline] Recipe cache update failed:', cacheErr);
    }

    // 4. Queue for backend sync
    try {
      await offlineQueueService.enqueue({
        entityType: EntityType.RECIPE,
        operation: OperationType.CREATE,
        tempId,
        payload: recipeData
      });

      return tempRecipe;
    } catch (err) {
      console.error('Error creating recipe:', err);
      // Remove temp recipe on immediate failure if online
      if (navigator.onLine) {
        setRecipes(prev => prev.filter(r => r.id !== tempId));
        try {
          await offlineDb.delete(STORES.RECIPES, tempId);
        } catch (e) { /* ignore */ }
      }
      setRecipesError(err.message);
      throw err;
    }
  };

  // Update existing recipe (with Offline-Queue support)
  const updateRecipe = async (id, recipeData) => {
    // Store previous state for rollback
    const previousRecipe = recipes.find(r => r.id === id || String(r.id) === String(id));

    // 1. Optimistic update in UI
    const updatedRecipe = {
      ...previousRecipe,
      ...recipeData,
      id,
      updatedAt: new Date().toISOString(),
      _pendingSync: true
    };

    setRecipes(prev => prev.map(r =>
      (r.id === id || String(r.id) === String(id)) ? updatedRecipe : r
    ));

    // 2. Save to IndexedDB cache
    try {
      await offlineDb.put(STORES.RECIPES, updatedRecipe);
    } catch (cacheErr) {
      console.warn('[Offline] Recipe cache update failed:', cacheErr);
    }

    // 3. Queue for backend sync
    try {
      await offlineQueueService.enqueue({
        entityType: EntityType.RECIPE,
        operation: OperationType.UPDATE,
        entityId: id,
        payload: recipeData
      });

      return updatedRecipe;
    } catch (err) {
      console.error('Error updating recipe:', err);
      // Rollback on immediate failure if online
      if (navigator.onLine && previousRecipe) {
        setRecipes(prev => prev.map(r =>
          (r.id === id || String(r.id) === String(id)) ? previousRecipe : r
        ));
        try {
          await offlineDb.put(STORES.RECIPES, previousRecipe);
        } catch (e) { /* ignore */ }
      }
      setRecipesError(err.message);
      throw err;
    }
  };

  // Delete recipe (with Offline-Queue support)
  const deleteRecipe = async (id) => {
    // Store previous state for rollback
    const previousRecipe = recipes.find(r => r.id === id || String(r.id) === String(id));

    // Papierkorb: gelöschtes Rezept immer archivieren, damit es wiederherstellbar bleibt
    // - awaited, damit das Archivieren VOR dem eigentlichen Löschen abgeschlossen ist
    if (previousRecipe) {
      await archiveItem(
        ENTITY_TYPES.RECIPE,
        ARCHIVE_REASONS.DELETED,
        previousRecipe,
        { label: previousRecipe.title }
      );
    }

    // 1. Optimistic update - remove from UI immediately
    setRecipes(prev => prev.filter(r => r.id !== id && String(r.id) !== String(id)));

    // 2. Remove from IndexedDB cache
    try {
      await offlineDb.delete(STORES.RECIPES, id);
    } catch (cacheErr) {
      console.warn('[Offline] Recipe cache delete failed:', cacheErr);
    }

    // 3. Queue for backend sync
    try {
      await offlineQueueService.enqueue({
        entityType: EntityType.RECIPE,
        operation: OperationType.DELETE,
        entityId: id
      });
    } catch (err) {
      console.error('Error deleting recipe:', err);
      // Rollback on immediate failure if online
      if (navigator.onLine && previousRecipe) {
        setRecipes(prev => [previousRecipe, ...prev]);
        try {
          await offlineDb.put(STORES.RECIPES, previousRecipe);
        } catch (e) { /* ignore */ }
      }
      setRecipesError(err.message);
      throw err;
    }
  };

  // Get recipe by bookmark ID
  const getRecipeByBookmarkId = async (bookmarkId) => {
    try {
      const recipe = await recipesApi.getByBookmarkId(bookmarkId);
      return recipe;
    } catch (err) {
      console.error('Error fetching recipe by bookmark:', err);
      return null;
    }
  };

  // Extract recipe from URL and save (for bookmark-based recipes)
  const extractRecipeFromUrl = async (url, bookmarkId, title) => {
    setRecipesLoading(true);
    setRecipesError(null);
    try {
      const extractedRecipe = await recipesApi.extractFromUrl(url, bookmarkId, title);

      // Transform backend format to frontend format
      // Backend uses: amount (String), Frontend expects: quantity (Number)
      const transformedRecipe = {
        ...extractedRecipe,
        // Map servings/baseServings
        baseServings: extractedRecipe.servings || extractedRecipe.baseServings || 4,
        // Transform ingredients
        ingredients: (extractedRecipe.ingredients || []).map(ing => ({
          ...ing,
          // Convert amount (String) to quantity (Number)
          quantity: parseFloat(ing.amount) || 0,
          // Keep unit and name
          unit: ing.unit || '',
          name: ing.name || '',
          note: ing.note || '',
          alternatives: ing.alternatives || []
        }))
      };

      // Add to local recipes list
      setRecipes(prev => {
        // Replace if already exists, otherwise add
        const exists = prev.find(r => r.bookmarkId === String(bookmarkId));
        if (exists) {
          return prev.map(r => r.bookmarkId === String(bookmarkId) ? transformedRecipe : r);
        }
        return [transformedRecipe, ...prev];
      });
      return transformedRecipe;
    } catch (err) {
      console.error('Error extracting recipe from URL:', err);
      setRecipesError(err.message);
      throw err;
    } finally {
      setRecipesLoading(false);
    }
  };

  // Scale ingredients based on servings
  const scaleIngredients = (ingredients, baseServings, targetServings) => {
    const scale = targetServings / baseServings;

    return ingredients.map(ingredient => {
      const scaledQuantity = ingredient.quantity * scale;
      let roundedQuantity;

      // Rounding logic based on unit
      if (ingredient.unit === 'g' || ingredient.unit === 'ml') {
        // Round to whole numbers for g/ml
        roundedQuantity = Math.round(scaledQuantity);
      } else if (ingredient.unit === 'Stück' || ingredient.unit === 'Stange') {
        // TODO: Consider rounding to 0.5 steps for pieces/stalks
        // For now, round to nearest 0.5
        roundedQuantity = Math.round(scaledQuantity * 2) / 2;
      } else if (ingredient.unit === 'TL' || ingredient.unit === 'EL') {
        // Round to nearest 0.25 for teaspoons/tablespoons
        roundedQuantity = Math.round(scaledQuantity * 4) / 4;
      } else {
        // Default: 2 decimal places
        roundedQuantity = Math.round(scaledQuantity * 100) / 100;
      }

      return {
        ...ingredient,
        quantity: roundedQuantity,
        originalQuantity: ingredient.quantity
      };
    });
  };

  // Trägt ein bereits vom Server bekanntes Rezept bzw. eine bereits bekannte
  // Einkaufsliste in den lokalen State ein, OHNE einen weiteren Backend-Call
  // zu machen. Wird von Archive.js nach einem erfolgreichen
  // POST /api/archive/{id}/restore aufgerufen - das Anlegen selbst hat das
  // Backend beim Restore bereits übernommen, ein erneuter createRecipe()/
  // createManualShoppingList()-Aufruf würde das Element doppelt anlegen.
  const addRestoredRecipe = (recipeObj) => {
    if (!recipeObj) return;
    setRecipes(prev => [recipeObj, ...prev]);
  };

  const addRestoredShoppingList = (listObj) => {
    if (!listObj) return;
    setShoppingLists(prev => [
      { ...listObj, checkedItems: new Set(listObj.checkedItems || []) },
      ...prev
    ]);
  };

  const value = {
    // Backend Recipes (CRUD)
    recipes,
    recipesLoading,
    recipesError,
    fetchRecipes,
    createRecipe,
    updateRecipe,
    deleteRecipe,
    getRecipeByBookmarkId,
    extractRecipeFromUrl,
    addRestoredRecipe,

    // Cart
    shoppingCart,
    addToCart,
    removeFromCart,
    clearCart,

    // Shopping lists (Backend)
    shoppingLists,
    shoppingListsLoading,
    shoppingListsError,
    fetchShoppingLists,
    createShoppingList,
    addRecipeToTodaysList,
    addRecipeIngredientsToList,
    createManualShoppingList,
    addItemToList,
    removeItemFromList,
    updateShoppingList,
    toggleItemChecked,
    deleteShoppingList,
    addRestoredShoppingList,

    // Share functions
    shareShoppingList,
    joinSharedList,
    revokeShoppingListShare,
    getShoppingListShareInfo,
    getActiveShares,
    removeCollaborator,
    leaveShoppingList,

    // Archive
    archivedRecipes,
    archiveRecipe,

    // Utilities
    getRecipeData,
    scaleIngredients,

    // Mock data for demo
    mockRecipeData: MOCK_RECIPE_DATA
  };

  return (
    <RecipeContext.Provider value={value}>
      {children}
    </RecipeContext.Provider>
  );
};
